export const ARTICLES_COMPRENDRE = [
  {
    path: "/comprendre/quest-ce-quun-pari-sportif",
    category: "Comprendre les paris",
    title: "Qu'est-ce qu'un pari sportif ? Définition et fonctionnement",
    description:
      "Définition, principe et déroulé d'un pari sportif en France : mise, cote, gain, règlement. Un guide simple pour débutants.",
    h1: "Qu'est-ce qu'un pari sportif ?",
    intro:
      "Un pari sportif consiste à miser une somme d'argent sur l'issue d'un événement sportif : le vainqueur d'un match, le nombre de buts, le classement d'une course... Si la prévision se réalise, le parieur reçoit un gain ; sinon, il perd sa mise. Ce gain dépend de la cote fixée par l'opérateur.",
    sections: [
      {
        id: "principe",
        h2: "Le principe en 4 éléments",
        blocks: [
          {
            t: "ol",
            items: [
              "<strong>L'événement</strong> : un match, une course, un tournoi.",
              "<strong>Le marché</strong> : ce sur quoi on parie (résultat final, nombre de buts, buteur, etc.).",
              "<strong>La cote</strong> : un nombre fixé par l'opérateur qui détermine le gain potentiel.",
              "<strong>La mise</strong> : la somme engagée par le joueur.",
            ],
          },
        ],
      },
      {
        id: "exemple",
        h2: "Un pari de A à Z (exemple chiffré)",
        blocks: [
          { t: "p", html: "Match de football : l'équipe A reçoit l'équipe B." },
          {
            t: "list",
            items: [
              "Vous pariez <strong>10 €</strong> sur la victoire de A, à la cote <strong>2,10</strong>.",
              "Si A gagne : gain total = 10 × 2,10 = <strong>21 €</strong> (dont 10 € de mise, soit un gain net de 11 €).",
              "Si A fait match nul ou perd : vous perdez vos 10 €.",
            ],
          },
        ],
      },
      {
        id: "difference",
        h2: "Pari sportif, pronostic, jeu de hasard : quelle différence ?",
        blocks: [
          {
            t: "p",
            html: "Un pronostic est une opinion sur l'issue d'un événement. Un pari transforme ce pronostic en engagement financier. Même si l'analyse sportive peut informer un choix, le résultat d'un événement sportif reste incertain : <strong>un pari est un jeu d'argent, et non un investissement</strong>.",
          },
        ],
      },
      {
        id: "ou-parier",
        h2: "Où peut-on parier en France ?",
        blocks: [
          {
            t: "list",
            items: [
              "En ligne chez les opérateurs <strong>agréés par l'ANJ</strong>.",
              "En points de vente physiques pour les paris proposés par la Française des Jeux (Parions Sport Point de Vente).",
              "Les paris sur des sites non agréés sont <strong>illégaux</strong> (voir la page « Site légal ou illégal ? »).",
            ],
          },
        ],
      },
      {
        id: "sur-quoi-parier",
        h2: "Sur quoi peut-on parier légalement ?",
        blocks: [
          {
            t: "p",
            html: "En France, les opérateurs ne peuvent proposer que des paris sur les compétitions autorisées par l'ANJ (la « liste sport », régulièrement mise à jour) et sur les types de paris autorisés. Le football, le rugby, le tennis ou le basket en font partie, mais certains événements sont exclus, notamment ceux à risque d'intégrité.",
          },
          {
            t: "callout",
            kind: "retain",
            html: "Un pari sportif est un jeu d'argent. Il ne garantit aucun revenu et comporte un risque de perte réel.",
          },
        ],
      },
    ],
    faq: [
      {
        q: "Un pari sportif est-il légal en France ?",
        a: "Oui, s'il est proposé par un opérateur agréé par l'ANJ et si le joueur est majeur.",
      },
      {
        q: "Peut-on vivre des paris sportifs ?",
        a: "Statistiquement, non : les cotes intègrent une marge qui rend l'espérance de gain négative pour le joueur sur le long terme.",
      },
      {
        q: "Quel est le montant minimal d'un pari ?",
        a: "Il varie selon les opérateurs, souvent quelques centimes à 1 €.",
      },
    ],
  },
  {
    path: "/comprendre/les-cotes",
    category: "Comprendre les paris",
    title: "Les cotes des paris sportifs expliquées simplement",
    description:
      "Comprendre les cotes décimales, calculer un gain, la probabilité implicite et la marge des opérateurs. Avec calculateur gratuit.",
    h1: "Les cotes des paris sportifs expliquées simplement",
    intro:
      "La cote est l'élément central d'un pari. Elle indique combien le parieur reçoit pour un euro misé et reflète, indirectement, la probabilité que l'opérateur attribue à un événement.",
    sections: [
      {
        id: "cote-decimale",
        h2: "La cote décimale (format utilisé en France)",
        blocks: [
          {
            t: "p",
            html: "Une cote de <strong>2,50</strong> signifie que pour 1 € misé, le gain total est de 2,50 € (mise incluse).",
          },
          {
            t: "list",
            items: [
              "<strong>Gain total = mise × cote</strong>",
              "<strong>Gain net = mise × (cote − 1)</strong>",
              "Exemple : 20 € à la cote 1,75 → gain total 35 €, gain net 15 €.",
            ],
          },
        ],
      },
      {
        id: "probabilite-implicite",
        h2: "La probabilité implicite",
        blocks: [
          {
            t: "p",
            html: "On peut convertir une cote en probabilité avec la formule : <strong>probabilité implicite = 1 ÷ cote</strong>.",
          },
          { t: "diagram", name: "ladder" },
          {
            t: "p",
            html: "Une cote basse signifie que l'événement est jugé probable ; une cote élevée, qu'il est jugé improbable.",
          },
        ],
      },
      {
        id: "marge",
        h2: "La marge de l'opérateur",
        blocks: [
          {
            t: "p",
            html: "Un opérateur ne propose pas des cotes « justes » : il y intègre une marge, qui est sa source de revenus. Exemple sur un match de football (1N2) :",
          },
          {
            t: "table",
            head: ["Résultat", "Cote", "Probabilité implicite"],
            rows: [
              ["Victoire A", "2,10", "47,6 %"],
              ["Match nul", "3,30", "30,3 %"],
              ["Victoire B", "3,40", "29,4 %"],
              ["<strong>Total</strong>", "", "<strong>107,3 %</strong>"],
            ],
          },
          { t: "diagram", name: "margin" },
          {
            t: "p",
            html: "Si les cotes étaient « justes », le total serait de 100 %. L'excédent (ici environ 7,3 points, soit une marge d'environ 6,8 %) représente la marge de l'opérateur.",
          },
        ],
      },
      {
        id: "evolution",
        h2: "Comment évoluent les cotes ?",
        blocks: [
          {
            t: "p",
            html: "Les cotes varient selon : l'évolution des mises (si beaucoup de joueurs misent sur un résultat, sa cote baisse), les informations (blessure, composition d'équipe, météo), le temps qui reste avant l'événement, et les analyses des opérateurs.",
          },
        ],
      },
      {
        id: "value",
        h2: "Cote et « value » : une notion à manier avec prudence",
        blocks: [
          {
            t: "p",
            html: "Le terme « value » désigne une cote jugée supérieure à la probabilité réelle d'un événement. Cette notion est théorique : la probabilité réelle est inconnue et les opérateurs disposent de moyens d'analyse considérables. Elle n'est en aucun cas une garantie de gain.",
          },
        ],
      },
      {
        id: "calculateur",
        h2: "Calculateur de cotes",
        blocks: [
          {
            t: "p",
            html: "Testez les formules avec vos propres chiffres : le calcul est instantané et aucune donnée n'est enregistrée.",
          },
          { t: "tool", name: "odds" },
        ],
      },
      {
        id: "erreurs",
        h2: "Erreurs fréquentes avec les cotes",
        blocks: [
          {
            t: "list",
            items: [
              "Penser qu'une cote élevée est « une opportunité » : elle traduit une faible probabilité.",
              "Oublier que le gain affiché comprend la mise.",
              "Comparer des cotes de marchés différents.",
            ],
          },
        ],
      },
    ],
    faq: [
      {
        q: "Existe-t-il d'autres formats de cotes ?",
        a: "Oui, les cotes fractionnaires (Royaume-Uni) et américaines, mais la cote décimale est le standard en France.",
      },
      {
        q: "Pourquoi les cotes diffèrent-elles d'un opérateur à l'autre ?",
        a: "Chaque opérateur fixe ses propres cotes et sa propre marge.",
      },
    ],
  },
  {
    path: "/comprendre/les-types-de-paris",
    category: "Comprendre les paris",
    title: "Les types de paris sportifs : simple, combiné, live, handicap",
    description:
      "Pari simple, combiné, système, live, handicap, plus/moins : comprendre chaque type de pari avec un exemple et ses risques.",
    h1: "Les types de paris sportifs",
    intro:
      "Il existe de nombreux types de paris. Pour chacun, voici le principe, un exemple chiffré et le point de vigilance à connaître.",
    sections: [
      {
        id: "pari-simple",
        h2: "Le pari simple",
        blocks: [
          { t: "p", html: "<strong>Principe</strong> : un seul pari sur un seul événement." },
          { t: "p", html: "<strong>Exemple</strong> : 10 € sur la victoire de A à la cote 1,80 → gain total 18 €." },
          { t: "p", html: "<strong>Vigilance</strong> : c'est le format le plus lisible et le moins risqué structurellement." },
        ],
      },
      {
        id: "pari-combine",
        h2: "Le pari combiné",
        blocks: [
          { t: "p", html: "<strong>Principe</strong> : plusieurs sélections regroupées ; toutes doivent être gagnantes ; les cotes se multiplient." },
          { t: "p", html: "<strong>Exemple</strong> : 3 sélections aux cotes 1,80 × 2,10 × 1,50 = cote totale 5,67. Pour 10 € misés → gain total 56,70 €." },
          { t: "p", html: "<strong>Vigilance</strong> : une seule sélection perdante et la mise est perdue. La probabilité implicite globale est ici de 17,6 %." },
          { t: "diagram", name: "combi" },
        ],
      },
      {
        id: "pari-systeme",
        h2: "Le pari système",
        blocks: [
          { t: "p", html: "<strong>Principe</strong> : plusieurs sélections combinées en plusieurs paris, permettant d'être payé même si une partie des sélections est perdante." },
          { t: "p", html: "<strong>Vigilance</strong> : la mise totale est multipliée par le nombre de paris générés." },
        ],
      },
      {
        id: "pari-live",
        h2: "Le pari live (en direct)",
        blocks: [
          { t: "p", html: "<strong>Principe</strong> : on parie pendant l'événement, avec des cotes qui évoluent en temps réel." },
          { t: "p", html: "<strong>Vigilance</strong> : rythme rapide, décisions impulsives, risque accru de dépassement de budget." },
        ],
      },
      {
        id: "pari-handicap",
        h2: "Le pari handicap",
        blocks: [
          { t: "p", html: "<strong>Principe</strong> : on attribue un avantage ou un désavantage artificiel à une équipe." },
          { t: "p", html: "<strong>Exemple</strong> : handicap −1 pour A : A doit gagner avec au moins 2 buts d'écart." },
        ],
      },
      {
        id: "over-under",
        h2: "Plus/moins (over/under)",
        blocks: [
          { t: "p", html: "<strong>Principe</strong> : on parie sur un total (buts, points, jeux) au-dessus ou en dessous d'un seuil." },
          { t: "p", html: "<strong>Exemple</strong> : « plus de 2,5 buts » gagne à partir de 3 buts." },
        ],
      },
      {
        id: "paris-speciaux",
        h2: "Les paris spéciaux",
        blocks: [
          { t: "p", html: "Buteur, premier but, nombre de corners, cartons, etc." },
          { t: "p", html: "<strong>Vigilance</strong> : ces marchés sont souvent assortis d'une marge plus importante." },
        ],
      },
      {
        id: "cash-out",
        h2: "Le cash-out",
        blocks: [
          { t: "p", html: "Fonction permettant de clôturer un pari avant la fin de l'événement pour récupérer une somme fixée par l'opérateur." },
          { t: "p", html: "<strong>Vigilance</strong> : le montant proposé intègre la marge de l'opérateur." },
        ],
      },
      {
        id: "recapitulatif",
        h2: "Tableau récapitulatif",
        blocks: [
          {
            t: "table",
            head: ["Type", "Complexité", "Niveau de risque", "Idéal pour"],
            rows: [
              ["Simple", "Faible", "Modéré", "Débuter"],
              ["Combiné", "Moyenne", "Élevé", "Connaître les cotes multipliées"],
              ["Système", "Élevée", "Élevé", "Joueurs avertis"],
              ["Live", "Moyenne", "Élevé", "Joueurs disciplinés"],
              ["Handicap", "Moyenne", "Modéré à élevé", "Comprendre les écarts"],
              ["Plus/moins", "Faible", "Modéré", "Débuter"],
            ],
          },
        ],
      },
      {
        id: "simulateur-combine",
        h2: "Simulateur de combiné",
        blocks: [
          {
            t: "p",
            html: "Composez votre propre combiné fictif (de 2 à 6 sélections) et observez l'effet sur la cote totale et la probabilité implicite.",
          },
          { t: "tool", name: "combi" },
        ],
      },
    ],
  },
  {
    path: "/comprendre/erreurs-et-mythes",
    category: "Comprendre les paris",
    title: "Paris sportifs : les erreurs de débutant et les mythes à éviter",
    description:
      "Courir après ses pertes, croire aux tuyaux, jouer des combinés à grosse cote... Les erreurs et idées reçues sur les paris sportifs.",
    h1: "Erreurs de débutant et idées reçues",
    sections: [
      {
        id: "erreurs-frequentes",
        h2: "Les erreurs les plus fréquentes",
        blocks: [
          {
            t: "ol",
            items: [
              "<strong>Courir après ses pertes.</strong> Augmenter ses mises pour « se refaire » est le mécanisme le plus courant d'endettement. Après une perte, l'événement suivant n'est pas « dû ».",
              "<strong>Miser sans budget défini.</strong> Fixer à l'avance une somme dédiée, perdue d'office, et ne jamais la dépasser.",
              "<strong>Multiplier les combinés à grosse cote.</strong> Attractifs, mais peu probables.",
              "<strong>Parier sur son équipe de cœur.</strong> L'affect biaise l'analyse.",
              "<strong>Parier en live sous l'émotion.</strong> Décisions rapides et impulsives.",
              "<strong>Négliger les conditions d'un bonus.</strong> Un bonus mal compris peut bloquer des fonds.",
              "<strong>Jouer fatigué, stressé ou sous l'effet de l'alcool.</strong>",
            ],
          },
        ],
      },
      {
        id: "mythes",
        h2: "Les mythes",
        blocks: [
          {
            t: "list",
            items: [
              "« Il existe une méthode infaillible. » <strong>Faux.</strong> Aucune méthode ne garantit des gains ; la marge de l'opérateur joue contre le joueur.",
              "« Les abonnements à des pronostiqueurs garantissent des gains. » <strong>Faux.</strong> Méfiez-vous des promesses de résultats. Aucun pronostic ne peut être garanti.",
              "« Une série de défaites annonce une victoire. » <strong>Faux.</strong> Les événements sont indépendants (erreur du joueur).",
              "« Les parieurs experts vivent des paris. » Très rares, et souvent limités ou exclus par les opérateurs.",
              "« Le bonus est de l'argent gratuit. » <strong>Faux</strong> : il est soumis à conditions.",
            ],
          },
          {
            t: "callout",
            kind: "retain",
            html: "Parier doit rester un loisir encadré par un budget. Si le jeu devient une source de stress ou d'endettement, il est temps d'en parler (voir « Jeu responsable »).",
          },
        ],
      },
    ],
  },
  {
    path: "/comprendre/les-maths-du-long-terme",
    category: "Comprendre les paris",
    title: "Pourquoi le joueur perd à long terme : l'espérance de gain expliquée",
    description:
      "Espérance de gain, marge de l'opérateur, loi des grands nombres : comprendre simplement pourquoi les paris sportifs sont défavorables au joueur.",
    h1: "Les maths du long terme : pourquoi le joueur est statistiquement perdant",
    intro:
      "On peut gagner un pari, ou même plusieurs. Mais sur un grand nombre de paris, les mathématiques sont défavorables au joueur. Voici pourquoi, sans jargon.",
    sections: [
      {
        id: "esperance",
        h2: "L'espérance de gain",
        blocks: [
          {
            t: "p",
            html: "L'espérance est le gain moyen attendu par pari, si on répétait ce pari un très grand nombre de fois.",
          },
          {
            t: "p",
            html: "<strong>Espérance = (probabilité de gagner × gain net) − (probabilité de perdre × mise)</strong>",
          },
        ],
      },
      {
        id: "exemple-pile-ou-face",
        h2: "Exemple : un pile ou face à la cote 1,90",
        blocks: [
          { t: "p", html: "Probabilité réelle : 50 % de gagner. Pour 10 € misés :" },
          {
            t: "list",
            items: ["gain net si succès : 9 €,", "perte si échec : 10 €."],
          },
          {
            t: "p",
            html: "Espérance = 0,5 × 9 − 0,5 × 10 = <strong>−0,50 € par pari</strong>, soit −5 % de la mise. Sur 100 paris de 10 € (1 000 € misés), la perte moyenne attendue est d'environ 50 €.",
          },
        ],
      },
      {
        id: "marge-travail",
        h2: "La marge fait le travail",
        blocks: [
          {
            t: "p",
            html: "Comme la somme des probabilités implicites dépasse 100 %, l'opérateur conserve en moyenne une part des sommes misées. Cette part est indépendante de qui gagne : elle vient de la structure des cotes.",
          },
        ],
      },
      {
        id: "grands-nombres",
        h2: "La loi des grands nombres",
        blocks: [
          {
            t: "p",
            html: "Sur peu de paris, la chance domine : on peut gagner. Plus le nombre de paris augmente, plus le résultat se rapproche de l'espérance mathématique. Autrement dit, jouer souvent augmente la probabilité de se rapprocher de la perte moyenne attendue.",
          },
        ],
      },
      {
        id: "et-les-bonus",
        h2: "Et les bonus ?",
        blocks: [
          {
            t: "p",
            html: "Un bonus peut atténuer la marge sur une courte période, mais les conditions qui l'accompagnent (multiplicateur de mise, cote minimale) peuvent limiter sa valeur réelle.",
          },
          {
            t: "callout",
            kind: "retain",
            html: "Les paris sportifs sont un divertissement payant. On peut en profiter en ayant conscience de son coût, comme pour un autre loisir, en fixant une limite.",
          },
        ],
      },
    ],
  },
];
